import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext, useWatch } from 'react-hook-form'
import {
  Upload01Icon,
  Trash01Icon,
  CheckCircleIcon,
  AlertCircleIcon,
  Loading01Icon,
  RefreshCw01Icon,
} from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'
import { Button } from '@/components/base/buttons'

type ValidationStatus = 'idle' | 'loading' | 'converting' | 'success' | 'error'

interface WhatsAppImageUploaderProps {
  className?: string
}

/**
 * WhatsApp Profile Image Uploader with validation.
 * 
 * WhatsApp requirements:
 * - Format: JPEG or PNG
 * - Minimum size: 192x192 pixels
 * - Maximum file size: 2MB
 * 
 * Supports automatic conversion of SVG and other formats to PNG.
 */
export function WhatsAppImageUploader({ className }: WhatsAppImageUploaderProps) {
  const { t } = useTranslation()
  const { setValue, control } = useFormContext()
  const inputRef = useRef<HTMLInputElement>(null)

  // Watch the image value from form
  const whatsappImage = useWatch({ control, name: 'config.whatsapp_image' })

  const [status, setStatus] = useState<ValidationStatus>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)

  // Sync local preview state with form value (server URL or post-save base64 object).
  // Clears pendingFile when the value is no longer a fresh upload, preventing
  // the setValue effect below from re-dirtying the form after a save.
  useEffect(() => {
    if (whatsappImage && typeof whatsappImage === 'string' && whatsappImage.startsWith('http')) {
      setPreviewUrl(whatsappImage)
      setStatus('success')
      setPendingFile(null)
    } else if (whatsappImage?.src) {
      setPreviewUrl(whatsappImage.src)
      setStatus('success')
      if (!whatsappImage.rawFile) {
        setPendingFile(null)
      }
    }
  }, [whatsappImage])

  // Convert image to PNG format
  const convertImageToPng = async (file: File): Promise<File | null> => {
    try {
      const tempUrl = URL.createObjectURL(file)
      
      try {
        const img = new Image()
        await new Promise((resolve, reject) => {
          img.onload = resolve
          img.onerror = reject
          img.src = tempUrl
        })
        
        const isSvg = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')
        let width = img.width
        let height = img.height
        
        // Scale SVGs for better resolution
        if (isSvg) {
          const scaleFactor = 3
          const aspectRatio = width / height
          const baseSize = Math.max(480, Math.max(width, height) * scaleFactor)
          
          if (width < height) {
            height = baseSize
            width = Math.round(height * aspectRatio)
          } else {
            width = baseSize
            height = Math.round(width / aspectRatio)
          }
        }
        
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        
        const ctx = canvas.getContext('2d')
        if (!ctx) throw new Error('Could not get canvas context')
        
        if (isSvg) {
          ctx.imageSmoothingEnabled = true
          ctx.imageSmoothingQuality = 'high'
        }
        
        ctx.drawImage(img, 0, 0, width, height)
        
        const blob = await new Promise<Blob | null>((resolve) => {
          canvas.toBlob(resolve, 'image/png', 1.0)
        })
        
        if (!blob) throw new Error('Failed to convert image to PNG')
        
        return new File([blob], file.name.replace(/\.[^.]+$/, '.png'), {
          type: 'image/png',
          lastModified: Date.now(),
        })
      } finally {
        URL.revokeObjectURL(tempUrl)
      }
    } catch {
      return null
    }
  }

  // Validate image
  const validateImage = useCallback(async (file: File): Promise<boolean> => {
    setStatus('loading')
    setErrorMessage(null)

    try {
      const tempUrl = URL.createObjectURL(file)
      
      try {
        // Load image to check dimensions
        const img = new Image()
        await new Promise((resolve, reject) => {
          img.onload = resolve
          img.onerror = reject
          img.src = tempUrl
        })

        // Check file type
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png']
        const isSvg = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')
        
        if (!validTypes.includes(file.type) && !isSvg) {
          setStatus('converting')
          const convertedFile = await convertImageToPng(file)
          
          if (convertedFile) {
            setPendingFile(convertedFile)
            setPreviewUrl(URL.createObjectURL(convertedFile))
            setStatus('success')
            return true
          } else {
            throw new Error(t('resources.assistants.whatsapp_image.invalid_format', { type: file.type }))
          }
        }

        if (isSvg) {
          setStatus('converting')
          const convertedFile = await convertImageToPng(file)
          
          if (convertedFile) {
            setPendingFile(convertedFile)
            setPreviewUrl(URL.createObjectURL(convertedFile))
            setStatus('success')
            return true
          } else {
            throw new Error(t('resources.assistants.whatsapp_image.svg_convert_failed'))
          }
        }

        if (img.naturalWidth < 192 || img.naturalHeight < 192) {
          throw new Error(t('resources.assistants.whatsapp_image.too_small', {
            width: img.naturalWidth,
            height: img.naturalHeight,
          }))
        }

        if (file.size > 2 * 1024 * 1024) {
          throw new Error(t('resources.assistants.whatsapp_image.too_large', {
            size: (file.size / (1024 * 1024)).toFixed(2),
          }))
        }

        // All validation passed
        setPendingFile(file)
        setPreviewUrl(tempUrl)
        setStatus('success')
        return true
      } catch (error) {
        URL.revokeObjectURL(tempUrl)
        throw error
      }
    } catch (error) {
      setStatus('error')
      setErrorMessage(error instanceof Error ? error.message : t('resources.assistants.whatsapp_image.validation_failed'))
      return false
    }
  }, [t])

  // Process a file (used by both click and drag/drop)
  const processFile = useCallback(async (file: File) => {
    await validateImage(file)
  }, [validateImage])

  // Handle file selection (click)
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    await processFile(file)
  }

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Only set to false if we're leaving the drop zone entirely
    if (e.currentTarget.contains(e.relatedTarget as Node)) return
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    const files = e.dataTransfer.files
    if (files.length === 0) return

    const file = files[0]
    if (!file.type.startsWith('image/')) {
      setStatus('error')
      setErrorMessage(t('resources.assistants.whatsapp_image.drop_image_only'))
      return
    }

    await processFile(file)
  }, [processFile, t])

  // Update form when pending file changes
  useEffect(() => {
    if (pendingFile && status === 'success') {
      setValue(
        'config.whatsapp_image',
        {
          rawFile: pendingFile,
          src: previewUrl,
          title: pendingFile.name,
        },
        { shouldDirty: true }
      )
    }
  }, [pendingFile, previewUrl, status, setValue])

  // Handle delete
  const handleDelete = () => {
    setValue('config.whatsapp_image', null, { shouldDirty: true })
    setPendingFile(null)
    setPreviewUrl(null)
    setStatus('idle')
    setErrorMessage(null)
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  // Handle retry
  const handleRetry = () => {
    setStatus('idle')
    setErrorMessage(null)
    inputRef.current?.click()
  }

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {/* Section header */}
      <div>
        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {t('resources.assistants.about.whatsapp_image')}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('resources.assistants.about.whatsapp_image_helper')}
        </p>
      </div>

      {/* Image preview or upload area */}
      {previewUrl && status === 'success' ? (
        <div className="flex flex-col gap-3">
          {/* Image preview */}
          <div className="w-fit">
            <img
              src={previewUrl}
              alt="WhatsApp profile"
              className="max-h-48 max-w-md rounded-lg border border-gray-200 object-contain dark:border-gray-700"
            />
          </div>

          {/* Validation status */}
          <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
            <CheckCircleIcon className="h-4 w-4" />
            {t('resources.assistants.whatsapp_image.valid')}
          </div>

          {/* Delete button */}
          <Button
            type="button"
            color="secondary"
            size="sm"
            onPress={handleDelete}
            className="w-fit text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/20"
            iconLeading={Trash01Icon}
          >
            {t('resources.generic.delete')}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {/* Upload area with drag and drop support */}
          <label
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={cn(
              'flex cursor-pointer flex-col items-center justify-center gap-2',
              'rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-6',
              'transition-colors duration-150',
              'hover:border-brand-500 hover:bg-gray-100',
              'dark:border-gray-600 dark:bg-gray-800 dark:hover:border-brand-400 dark:hover:bg-gray-700',
              isDragOver && 'border-brand-500 bg-brand-50 dark:border-brand-400 dark:bg-brand-900/20',
              status === 'error' && 'border-red-300 dark:border-red-600'
            )}
          >
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/gif,image/webp"
              onChange={handleFileChange}
              className="hidden"
            />
            <Upload01Icon className={cn(
              'h-8 w-8 text-gray-400 transition-colors',
              isDragOver && 'text-brand-500 dark:text-brand-400'
            )} />
            <span className={cn(
              'text-sm text-gray-600 dark:text-gray-400',
              isDragOver && 'text-brand-600 dark:text-brand-400'
            )}>
              {isDragOver ? t('resources.assistants.whatsapp_image.drop_here') : t('resources.assistants.whatsapp_image.click_to_upload')}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-500">
              {t('resources.assistants.whatsapp_image.formats_hint')}
            </span>
          </label>

          {/* Loading state */}
          {(status === 'loading' || status === 'converting') && (
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <Loading01Icon className="h-4 w-4 animate-spin" />
              {status === 'converting' ? t('resources.assistants.whatsapp_image.converting') : t('resources.assistants.whatsapp_image.validating')}
            </div>
          )}

          {/* Error state */}
          {status === 'error' && errorMessage && (
            <div className="flex items-center gap-2">
              <AlertCircleIcon className="h-4 w-4 flex-shrink-0 text-red-500" />
              <span className="flex-1 text-sm text-red-600 dark:text-red-400">
                {errorMessage}
              </span>
              <Button
                type="button"
                color="secondary"
                size="sm"
                onPress={handleRetry}
                iconLeading={RefreshCw01Icon}
              >
                {t('resources.assistants.whatsapp_image.retry')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default WhatsAppImageUploader
